/**
 * src/components/MobileOnlyRoute.tsx — customer screens are a phone product.
 *
 * Attic's customer experience is the mobile app. The web SPA exists because
 * some flows genuinely need a browser (password reset lands from an email, the
 * consent gate, account deletion), and because drivers and admins work here —
 * but the crate-management screens were never designed for a desktop viewport
 * and render badly on one.
 *
 * Rather than show a broken layout, desktop visitors get told where the product
 * actually lives.
 *
 * ── What this does NOT block ───────────────────────────────────────────────
 * It is mounted inside <CustomerRoleRoute>, so it only ever wraps customer
 * screens. Deliberately still reachable on desktop:
 *
 *   /profile/*        shared with drivers and admins — settings and sign-out
 *   /profile/legal    documents must be readable anywhere
 *   /profile/delete   Google Play requires deletion reachable outside the app
 *   /profile/password password reset arrives by email, i.e. in a browser
 *   /login            the entry point for all three roles
 *   /driver/*, /admin/*  the whole reason the web app exists
 *
 * That list is the point: this is a redirect for people in the wrong place,
 * not a lockout. Blocking a flow that legitimately needs a browser would break
 * real users and generate support load.
 */

import { Outlet } from 'react-router';
import { useEffect, useState } from 'react';

const STORE_IOS = 'https://apps.apple.com/app/id6788506073';
const STORE_ANDROID = 'https://play.google.com/store/apps/details?id=com.attic.app';

/**
 * Escape hatch. `?desktop=1` once, and this browser keeps customer screens for
 * the session — so support and QA can reproduce what a customer reported
 * without anyone having to ship a change. Session-scoped on purpose: it should
 * not quietly persist on a machine and mask the interception forever.
 */
const OVERRIDE_KEY = 'attic:allow-desktop';

/**
 * "Has a mouse" rather than "is a small screen". A narrow desktop window is
 * still a desktop, and a phone in landscape is still a phone — width alone
 * gets both wrong. hover+fine-pointer is the capability that actually
 * distinguishes them.
 */
function isDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  if (sessionStorage.getItem(OVERRIDE_KEY) === '1') return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

export function MobileOnlyRoute() {
  const [blocked, setBlocked] = useState(isDesktop);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('desktop') === '1') {
      sessionStorage.setItem(OVERRIDE_KEY, '1');
      setBlocked(false);
    }
  }, []);

  // Re-evaluate if the capability changes — a detachable keyboard or a device
  // switching modes flips this without a reload.
  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const onChange = () => setBlocked(isDesktop());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  if (!blocked) return <Outlet />;

  return (
    <main
      style={{
        minHeight: '100dvh', display: 'grid', placeItems: 'center',
        padding: '48px 24px', background: 'var(--bg, var(--bg))', color: 'var(--ink, var(--attic-ink))',
      }}
    >
      <div style={{ maxWidth: '32rem', textAlign: 'center' }}>
        <p style={{
          fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase',
          opacity: 0.6, margin: '0 0 12px',
        }}>
          Attic
        </p>
        <h1 style={{
          fontFamily: 'var(--attic-font-display)', fontSize: 'clamp(28px, 5vw, 40px)',
          lineHeight: 1.1, margin: '0 0 12px', letterSpacing: '-0.5px',
        }}>
          Attic lives on your phone
        </h1>
        <p style={{ fontSize: 17, lineHeight: 1.6, opacity: 0.85, margin: '0 0 32px' }}>
          Your crates are managed in the app, where it's built for a phone and your
          camera is a tap away for scanning. There's nothing to do here on a desktop.
        </p>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href={STORE_IOS} style={{ textDecoration: 'underline', fontSize: 15 }}>
            Download for iPhone
          </a>
          <a href={STORE_ANDROID} style={{ textDecoration: 'underline', fontSize: 15 }}>
            Download for Android
          </a>
        </div>

        {/* Everything a customer might legitimately need from a desktop — and
            the reason this is not a hard block. Someone with a lost or broken
            phone still has a route through. */}
        <p style={{ marginTop: 40, fontSize: 14, lineHeight: 1.7, opacity: 0.7 }}>
          Need something without your phone? You can still{' '}
          <a href="/profile/payment">manage billing</a>,{' '}
          <a href="/profile/legal">read our terms</a>, or{' '}
          <a href="/profile">change account settings</a>.
          <br />
          Anything else, email{' '}
          <a href="mailto:hello@heyattic.com">hello@heyattic.com</a> and we'll sort it out.
        </p>
      </div>
    </main>
  );
}
